import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldOff, Trash2, Loader2, Plus } from "lucide-react";
import { api } from "@/lib/api";
import IntelligenceHub from "./IntelligenceHub";
import { SectionTitle } from "./atoms";

const inputCls =
  "w-full rounded-md bg-[#F8F7F3] border border-[#E4E1D8] focus:border-[#0B2545] focus:ring-1 focus:ring-[#0B2545] outline-none px-3 py-2 text-sm text-[#1F2937]";

export default function ComplianceCenter() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ scope: "personal", match_email: "", match_phone: "", match_linkedin: "", reason: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/cdl/dnc");
      setItems(data.items || []);
    } catch {
      toast.error("Chargement DNC impossible.");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!f.reason.trim()) return toast.error("Motif requis.");
    if (!f.match_email && !f.match_phone && !f.match_linkedin) {
      return toast.error("Au moins un identifiant (email / téléphone / LinkedIn) requis.");
    }
    setSaving(true);
    try {
      await api.post("/cdl/dnc", f);
      toast.success("Entrée ajoutée à la liste « Ne pas contacter ».");
      setF({ scope: "personal", match_email: "", match_phone: "", match_linkedin: "", reason: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ajout impossible.");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!confirm("Supprimer cette entrée ?")) return;
    try {
      await api.delete(`/cdl/dnc/${id}`);
      load();
    } catch (e) {
      toast.error("Suppression impossible.");
    }
  };

  return (
    <IntelligenceHub activeTab="dnc">
      <div className="max-w-3xl space-y-8">
        <div className="rounded-md border border-[#E4E1D8] bg-white p-5">
          <div className="flex items-start gap-3">
            <ShieldOff className="h-5 w-5 text-rose-600 mt-0.5" strokeWidth={1.8} />
            <div className="flex-1">
              <div className="text-sm font-medium text-[#0B2545]">Liste interne « Ne pas contacter »</div>
              <p className="mt-1 text-xs text-[#5B5B62]">
                Toute cible dont l&apos;email, le téléphone ou l&apos;URL LinkedIn correspond à une entrée ci-dessous
                sera automatiquement marquée <code>do_not_contact</code>. Le moteur bloque alors toute action
                de contact enregistrée sur cette cible.
              </p>
            </div>
          </div>
        </div>

        <div>
          <SectionTitle sub="Ajouter une nouvelle exclusion.">Ajouter une entrée</SectionTitle>
          <div className="rounded-md border border-[#E4E1D8] bg-white p-4 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="block">
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#5B5B62] mb-1.5">Portée</div>
                <select value={f.scope} onChange={(e) => setF({...f, scope: e.target.value})} className={inputCls} data-testid="dnc-scope">
                  <option value="personal">Privée (moi uniquement)</option>
                  <option value="org">Organisation entière</option>
                </select>
              </label>
              <label className="block">
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#5B5B62] mb-1.5">Motif *</div>
                <input data-testid="dnc-reason" value={f.reason} onChange={(e) => setF({...f, reason: e.target.value})} className={inputCls} placeholder="Ex : opt-out explicite le 2026-02-08" />
              </label>
              <label className="block">
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#5B5B62] mb-1.5">Courriel</div>
                <input data-testid="dnc-email" value={f.match_email} onChange={(e) => setF({...f, match_email: e.target.value})} className={inputCls} placeholder="prenom@example.com" />
              </label>
              <label className="block">
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#5B5B62] mb-1.5">Téléphone</div>
                <input data-testid="dnc-phone" value={f.match_phone} onChange={(e) => setF({...f, match_phone: e.target.value})} className={inputCls} placeholder="+15140000000" />
              </label>
              <label className="block md:col-span-2">
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#5B5B62] mb-1.5">LinkedIn</div>
                <input data-testid="dnc-linkedin" value={f.match_linkedin} onChange={(e) => setF({...f, match_linkedin: e.target.value})} className={inputCls} placeholder="https://linkedin.com/in/…" />
              </label>
            </div>
            <div className="flex justify-end">
              <button
                onClick={add}
                disabled={saving}
                data-testid="dnc-save"
                className="inline-flex items-center gap-1.5 rounded-md bg-[#0B2545] hover:bg-[#DCC29A] text-white font-medium px-4 py-2 text-sm transition-colors disabled:opacity-60"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.8} /> : <Plus className="h-4 w-4" strokeWidth={1.8} />}
                Ajouter à la liste
              </button>
            </div>
          </div>
        </div>

        <div>
          <SectionTitle sub={items.length + ' entrée' + (items.length > 1 ? 's' : '') + ' — la vérification est automatique lors du prochain contrôle.'}>
            Entrées actives
          </SectionTitle>
          {loading ? (
            <div className="flex items-center justify-center py-8 text-[#5B5B62]">
              <Loader2 className="h-4 w-4 animate-spin mr-2" strokeWidth={1.8} /> Chargement…
            </div>
          ) : items.length === 0 ? (
            <div className="rounded-md border border-dashed border-[#E4E1D8] bg-white/40 px-4 py-8 text-center text-xs text-[#5B5B62]">
              Aucune entrée pour l&apos;instant.
            </div>
          ) : (
            <div className="rounded-md border border-[#E4E1D8] bg-white divide-y divide-[#F2EFE7]">
              {items.map((it) => (
                <div key={it.dnc_id} className="flex items-start gap-3 px-4 py-3">
                  <div className="flex-1">
                    <div className="text-sm text-[#1F2937]">{it.match_email || it.match_phone || it.match_linkedin}</div>
                    <div className="text-[11px] text-[#5B5B62] mt-0.5">
                      {it.scope === "org" ? "Portée organisation" : "Privée"} · {it.reason}
                    </div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B8B8F] mt-1">
                      {new Date(it.created_at).toLocaleString("fr-CA")}
                    </div>
                  </div>
                  <button onClick={() => remove(it.dnc_id)} className="text-[#8B8B8F] hover:text-rose-600">
                    <Trash2 className="h-4 w-4" strokeWidth={1.8} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </IntelligenceHub>
  );
}
